import { useEffect, useState } from "react"
import AuthenticatedLayout from "@components/layout/Authenticated"
import { Box, Text, HStack, Button, ButtonGroup, MenuItem, VStack, Radio, StackDivider, Input, 
    Textarea, Center, IconButton, useDisclosure, RadioGroup, Checkbox, List, ListItem, Stack, Select,
    Switch
} from "@chakra-ui/react"
import { RiAddFill, RiCloseFill} from "@remixicon/react"
import { ModalAsset, SelectAsset} from "@utils/assets"
import { FieldControl } from "@components/form"
import countries from "@utils/countries.json"
import rules from "@utils/rules.json"
import { useValidator } from "@handlers/index"
import { useSettingsService } from "@services/index"
import { isArrayEqual, debounce } from "@utils/helpers"

const ShippingFeeForm = ({isOpen, onClose, shipping, country, setItem, getShippings}) => {

    const [errors, setError] = useState({})
    const [currentCountry, setCurrentCountry] = useState(country ?? 'Nigeria');
    const [data, setData] = useState([
        {
            country: country ?? 'Nigeria',
            fees: [],
        },
    ]);

    const { updateShippingFees, response:new_shipping, requestErrors, load } = useSettingsService()
    const {validate, validationErrors} = useValidator(rules.create_shipping_fee)

    //Adds Validation Errors to Error State
    useEffect(() => {
        setError(validationErrors)
    }, [validationErrors])

    //Adds Request Errors to Error State
    useEffect(() => {
        setError(requestErrors)
    }, [requestErrors])

    useEffect(() => {
        if (new_shipping?.shipping) {
            if(setItem) setItem(new_shipping?.shipping)
            if(getShippings) getShippings()
        }
    }, [new_shipping])

    useEffect(() => {
        if (shipping?.fees?.length > 0) {
            setCurrentCountry(currentCountry)
            setData(shipping?.fees)
        }
    }, [shipping])

    const addState = (country) => {
        setData((prevData) =>
            prevData.some((item) => item.country === country)
                ? prevData.map((item) =>
                    item.country === country
                        ? { ...item, fees: [...item.fees, { state: '', lgas: [], fee: 0 }] }
                        : item
                )
                : [...prevData, { country, fees: [{ state: '', lgas: [], fee: 0 }] }]
        );
    };

    const removeState = (country, stateIndex) => {
        setData((prevData) =>
            prevData
                .map((item) =>
                    item.country === country
                        ? { ...item, fees: item.fees.filter((_, index) => index !== stateIndex) }
                        : item
                )
                .filter((item) => item.fees.length > 0)
        );
    };

    const updateStateOrLGA = (country, stateIndex, key, value) => {
        setData((prevData) =>
            prevData.map((item) =>
                item.country === country
                    ? {
                        ...item,
                        fees: item.fees.map((fee, idx) =>
                            idx === stateIndex ? { ...fee, [key]: value } : fee
                        ),
                    }
                    : item
            )
        );
    };

    const onHandleStateChange = (country, name, index, value) => {
        updateStateOrLGA(country, index, name, value);
    };
    
    const onHandleLGAChange = (country, stateIndex, selectedLGAs) => {
        updateStateOrLGA(country, stateIndex, 'lgas', selectedLGAs);
    };

    const getOptions = (type, country, state, index) => {
        const countryData = countries.find((c) => c.name === country);
    
        if (type === 'state') {
            return countryData?.states?.filter((state) => {
                const stateLGAs = state?.lgas || [state?.name];
                const selectedLGAs = data?.find((fees) => fees?.country === country)?.fees
                    .filter((_, idx) => idx !== index)
                    .flatMap((fee) => fee?.lgas || []);
                return !stateLGAs.every((lga) => selectedLGAs.includes(lga)); 
            })?.map((state) => ({
                value: state.name,
                label: state.name,
            })) || [];
        }
    
        if (type === 'lga') {
            const selectedLGAs = data?.find((fees) => fees?.country === country)?.fees
                .filter((_, idx) => idx !== index)
                .flatMap((fee) => fee?.lgas || []);
    
            const stateData = countryData?.states?.find((s) => s.name === state);
            const lgas = stateData?.lgas || [state];
    
            return lgas
                .filter((lga) => !selectedLGAs.includes(lga))
                .map((lga) => ({
                    value: lga?.name ?? lga,
                    label: lga?.name ?? lga,
                })) || [];
        }
    
        return [];
    };

    const submit = () => {
        if (!isDisabled) {
            updateShippingFees({fees: data, id: shipping?.id})
        }
    }

    const isDisabled = load || 
    data?.some((countryData) => 
        !countryData.country || 
        countryData.fees.some((fee) => 
            !fee.state || 
            !fee.lgas.length || 
            !fee.fee 
        )
    ) || 
    Object.keys(errors).length > 0; 

    return (
        
        <ModalAsset
            arrowless
            modalProps={{isOpen, onClose, isCentered: true, size: {base: 'full', lg: '3xl'}, closeOnOverlayClick: false}}
            headerContent={
            <Box>
                <Text className="font-semibold">Shipping Rate Configuration</Text>
                <Text className="text-xs font-normal text-gray-400">
                    Set or update the shipping rates for specific regions, states, or delivery zones to ensure accurate cost calculations.
                </Text>
            </Box>
            }
            bodyContent={
                <VStack spacing={7} className="!items-start !justify-center">
                                        
                    <SelectAsset 
                        value={data.find((item) => item.country === currentCountry)?.country}
                        options={countries?.map?.((country) => ({
                            value: country?.name,
                            label: country?.name,
                        }))}
                        onChange={(selected) => {
                            setCurrentCountry(selected);
                        }}
                        isSearch
                        placeholder={"Select Country"}
                        size={'md'}
                    />
                    

                    <FieldControl error={errors?.fees?.[0]} formProps={{className: 'mb-2'}} label={'Set Fee'}>
                        <VStack spacing={5} className="!justify-between !items-start w-full">
                            {
                                (
                                    data?.find((fees) => fees?.country === currentCountry)?.fees || []).map((fee, index) => {

                                        return (
                                   
                                            <Stack key={index} direction={['column', 'row']} className="w-full">

                                                <SelectAsset 
                                                    value={fee?.state}
                                                    options={getOptions('state', currentCountry, null, index)}
                                                    onChange={(selected) => onHandleStateChange(currentCountry, 'state', index, selected)}
                                                    isSearch
                                                    placeholder={'Select State'}
                                                />
                                                
                                                <SelectAsset 
                                                    value={fee?.lgas}
                                                    options={getOptions('lga', currentCountry, fee?.state, index)}
                                                    onChange={(selected) => {
                                                        onHandleLGAChange(currentCountry, index, selected);
                                                    }}
                                                    isSearch
                                                    isMulti
                                                    placeholder={'Select LGAs / Cities'}
                                                />

                                                <FieldControl leftElement={
                                                    <Box className="px-2 border-r">
                                                        <Text className="text-sm">₦</Text>
                                                    </Box>
                                                }>
                                                    <Input
                                                        size={{ base: 'lg', lg: 'md' }} 
                                                        fontSize={'xs'} 
                                                        type="number"
                                                        placeholder="Enter Shipping Fee"
                                                        value={fee?.fee}
                                                        onChange={(event) => onHandleStateChange(currentCountry, 'fee', index, event?.target?.value)}
                                                    />
                                                </FieldControl>

                                                <HStack className="w-1/6 justify-end">
                                                    <IconButton 
                                                        className="!bg-red-500 !text-white" 
                                                        size={'sm'} 
                                                        icon={<RiCloseFill size={20} />} 
                                                        onClick={() => {
                                                            removeState(currentCountry, index)
                                                        }}
                                                    />
                                                </HStack>
                        
                                            </Stack>

                                        )
                                    }
                                )
                            }
                            <HStack className="items-center">
                                <IconButton onClick={() => addState(currentCountry)} className="!bg-primary-950 !text-white" size={'sm'} icon={<RiAddFill size={20} />} />
                                <Text className="text-xs">Add Fees</Text>
                            </HStack>
                        </VStack>
                    </FieldControl>
                    
                </VStack>
            }
            footerContent={
                <ButtonGroup>
                    <Button size={'sm'} onClick={onClose} className="border">Cancel</Button>
                    <Button size={'sm'} onClick={submit} isDisabled={isDisabled} isLoading={load} className="!bg-primary-950 !text-white">Save Changes</Button>
                </ButtonGroup>
            }

        />
    )

}


export default ShippingFeeForm